/**
 * SitesManager — "Manage Sites" card on the Context Engine page.
 * Add / rename / re-context / delete sites. Basic tier gets one site
 * with a Pro upsell; Pro tier gets multi-site.
 */
import { useState } from "react";

export function SitesManager({
  sites, tier, maxSites, activeSiteId, onSelectSite,
  addSite, updateSite, deleteSite,
}) {
  const [name, setName] = useState("");
  const [context, setContext] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editContext, setEditContext] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const atLimit = sites.length >= maxSites;

  const submitAdd = async () => {
    setAdding(true);
    setError(null);
    try {
      const site = await addSite(name.trim(), context.trim());
      setName("");
      setContext("");
      if (site?.site_id) onSelectSite(site.site_id);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.site_id);
    setEditName(s.name);
    setEditContext(s.site_context || "");
  };

  const saveEdit = async (siteId) => {
    setBusyId(siteId);
    setError(null);
    try {
      await updateSite(siteId, {
        name: editName.trim(),
        site_context: editContext.trim(),
      });
      setEditingId(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const removeSite = async (siteId) => {
    setBusyId(siteId);
    setError(null);
    try {
      await deleteSite(siteId);
      setConfirmDeleteId(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="beacon-dashboard-card beacon-sites-manager"
      data-testid="sites-manager">
      <div className="beacon-dashboard-card-head">
        <div>
          <div className="beacon-tiny-label">MANAGE SITES</div>
          <p className="beacon-sites-manager-lede">
            Run a separate annual plan per site — head office, workshop,
            the mine. Company context carries over; each site adds its
            own notes so the calendar reflects that site&rsquo;s risks.
          </p>
        </div>
        <span className={`beacon-tier-chip is-${tier}`} data-testid="sites-tier-chip">
          {tier === "pro" ? "Pro" : "Basic"} · {sites.length}/{maxSites} site{maxSites === 1 ? "" : "s"}
        </span>
      </div>

      {error && (
        <div className="beacon-tab-error" data-testid="sites-manager-error">{error}</div>
      )}

      <ul className="beacon-sites-list" data-testid="sites-list">
        {sites.map((s) => {
          const busy = busyId === s.site_id;
          const isActive = s.site_id === activeSiteId;
          if (editingId === s.site_id) {
            return (
              <li key={s.site_id} className="beacon-site-row is-editing"
                data-testid={`site-row-${s.site_id}`}>
                <div className="beacon-site-edit-fields">
                  <input className="beacon-input" value={editName}
                    data-testid={`site-edit-name-${s.site_id}`}
                    maxLength={120}
                    onChange={(e) => setEditName(e.target.value)} />
                  <input className="beacon-input" value={editContext}
                    data-testid={`site-edit-context-${s.site_id}`}
                    maxLength={300}
                    placeholder="Site context — e.g. mechanical workshop, 12 fitters, heavy plant servicing"
                    onChange={(e) => setEditContext(e.target.value)} />
                </div>
                <div className="beacon-site-row-actions">
                  <button type="button" className="beacon-cta beacon-cta-compact"
                    data-testid={`site-save-${s.site_id}`}
                    disabled={busy || !editName.trim()}
                    onClick={() => saveEdit(s.site_id)}>
                    {busy ? "Saving…" : "Save"}
                  </button>
                  <button type="button" className="beacon-secondary-btn"
                    data-testid={`site-cancel-edit-${s.site_id}`}
                    onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              </li>
            );
          }
          return (
            <li key={s.site_id}
              className={`beacon-site-row${isActive ? " is-active" : ""}`}
              data-testid={`site-row-${s.site_id}`}>
              <div>
                <div className="beacon-site-row-name">
                  {s.name}
                  {s.is_default && (
                    <span className="beacon-site-default-chip">default</span>
                  )}
                  {isActive && (
                    <span className="beacon-site-active-chip"
                      data-testid={`site-active-chip-${s.site_id}`}>active</span>
                  )}
                </div>
                {s.site_context && (
                  <div className="beacon-site-row-context">{s.site_context}</div>
                )}
              </div>
              <div className="beacon-site-row-actions">
                {!isActive && (
                  <button type="button" className="beacon-secondary-btn"
                    data-testid={`site-select-${s.site_id}`}
                    onClick={() => onSelectSite(s.site_id)}>Switch to</button>
                )}
                <button type="button" className="beacon-secondary-btn"
                  data-testid={`site-edit-${s.site_id}`}
                  onClick={() => startEdit(s)}>Edit</button>
                {!s.is_default && (
                  confirmDeleteId === s.site_id ? (
                    <>
                      <button type="button"
                        className="beacon-secondary-btn beacon-admin-btn-danger"
                        data-testid={`site-confirm-delete-${s.site_id}`}
                        disabled={busy}
                        onClick={() => removeSite(s.site_id)}>Confirm</button>
                      <button type="button" className="beacon-secondary-btn"
                        data-testid={`site-cancel-delete-${s.site_id}`}
                        onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                    </>
                  ) : (
                    <button type="button"
                      className="beacon-secondary-btn beacon-admin-btn-danger"
                      data-testid={`site-delete-${s.site_id}`}
                      onClick={() => setConfirmDeleteId(s.site_id)}>Delete</button>
                  )
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {atLimit && tier === "basic" ? (
        <div className="beacon-sites-pro-upsell" data-testid="sites-pro-upsell">
          <strong>Multi-site plans are a Pro feature.</strong> Your Basic
          plan includes one site — upgrade to Pro to build separate annual
          plans for the workshop, the mine, head office and more.
        </div>
      ) : atLimit ? (
        <div className="beacon-sites-pro-upsell" data-testid="sites-limit-note">
          Site limit reached ({maxSites} sites on the Pro plan).
        </div>
      ) : (
        <div className="beacon-site-add" data-testid="site-add-form">
          <div className="beacon-tiny-label">ADD A SITE</div>
          <div className="beacon-site-add-fields">
            <input className="beacon-input"
              data-testid="add-site-name"
              value={name}
              maxLength={120}
              placeholder="Site name — e.g. Mechanical Workshop"
              onChange={(e) => setName(e.target.value)} />
            <input className="beacon-input"
              data-testid="add-site-context"
              value={context}
              maxLength={300}
              placeholder="Optional context — e.g. heavy plant servicing, 12 fitters, day shift only"
              onChange={(e) => setContext(e.target.value)} />
            <button type="button" className="beacon-cta beacon-cta-compact"
              data-testid="add-site-submit"
              disabled={!name.trim() || adding}
              onClick={submitAdd}>
              {adding ? "Adding…" : "Add site →"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
